import { useLayoutEffect, useRef, useState } from 'react'
import { createGesture, type GestureOptions, type Point } from './gesture'

type Resolve = (target: Element, point: Point, mouse: boolean) => GestureOptions | null
// Native bubbling gives the innermost eligible surface first refusal.
const claimed = new WeakSet<Event>()

export function bindSurfaceGesture(root: HTMLElement, resolve: Resolve, suppression: { current: boolean }) {
  let gesture: ReturnType<typeof createGesture> | null = null
  let id: number | null = null
  let touching = false
  const cancel = () => { gesture?.cancel(); gesture = null; id = null }
  const begin = (event: Event, point: Point, nextId: number, touch: boolean, mouse: boolean) => {
    if (claimed.has(event) || !(event.target instanceof Element)) return
    suppression.current = false
    // Leave the browser's edge navigation alone.
    if (!mouse && (point.x < 24 || point.x > window.innerWidth - 24)) return
    const options = resolve(event.target, point, mouse)
    if (!options) return
    claimed.add(event)
    cancel()
    id = nextId; touching = touch
    gesture = createGesture(point, { ...options, onActivate: () => { suppression.current = true; options.onActivate() } })
  }
  const pointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' && event.button === 0 && event.isPrimary)
      begin(event, { x: event.clientX, y: event.clientY }, event.pointerId, false, event.pointerType === 'mouse')
  }
  const touchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) { cancel(); return }
    const touch = event.touches[0]
    begin(event, { x: touch.clientX, y: touch.clientY }, touch.identifier, true, false)
  }
  const move = (event: Event, point: Point) => {
    if (!event.cancelable) { cancel(); return }
    if (gesture?.move(point)) event.preventDefault()
  }
  const pointerMove = (event: PointerEvent) => { if (!touching && id === event.pointerId) move(event, { x: event.clientX, y: event.clientY }) }
  const touchMove = (event: TouchEvent) => {
    if (!touching) return
    if (event.touches.length !== 1) { cancel(); return }
    const touch = Array.from(event.touches).find((touch) => touch.identifier === id)
    if (touch) move(event, { x: touch.clientX, y: touch.clientY })
  }
  const end = (event: Event) => {
    if (gesture?.active && event.cancelable) event.preventDefault()
    gesture?.release(); gesture = null; id = null
  }
  const pointerEnd = (event: PointerEvent) => { if (!touching && event.pointerId === id) end(event) }
  const touchEnd = (event: TouchEvent) => { if (touching && Array.from(event.changedTouches).some((touch) => touch.identifier === id)) end(event) }
  // Touch gestures use Touch Events. Browsers can cancel their parallel Pointer
  // Events stream during panning without cancelling the touch sequence itself.
  const pointerCancel = (event: PointerEvent) => { if (!touching && event.pointerId === id) cancel() }
  const touchCancel = (event: TouchEvent) => { if (touching && Array.from(event.changedTouches).some((touch) => touch.identifier === id)) cancel() }
  const multitouch = (event: TouchEvent) => { if (event.touches.length > 1) cancel() }
  const click = (event: MouseEvent) => {
    if (suppression.current && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); suppression.current = false }
  }
  const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && gesture?.active) { event.preventDefault(); event.stopPropagation(); cancel() } }
  const context = (event: Event) => { if (gesture) event.preventDefault() }
  const listeners: [EventTarget, string, EventListener, (AddEventListenerOptions | boolean)?][] = []
  function on(target: EventTarget, type: string, listener: unknown, options?: AddEventListenerOptions | boolean) {
    target.addEventListener(type, listener as EventListener, options)
    listeners.push([target, type, listener as EventListener, options])
  }
  on(root, 'pointerdown', pointerDown)
  on(root, 'touchstart', touchStart, { passive: true })
  on(root, 'click', click, true)
  on(root, 'contextmenu', context)
  on(window, 'touchstart', multitouch, { passive: true })
  on(window, 'pointermove', pointerMove, { passive: false })
  on(window, 'touchmove', touchMove, { passive: false })
  on(window, 'pointerup', pointerEnd)
  on(window, 'touchend', touchEnd, { passive: false })
  on(window, 'pointercancel', pointerCancel)
  on(window, 'touchcancel', touchCancel)
  on(window, 'blur', cancel)
  on(window, 'resize', cancel)
  on(window, 'keydown', key, true)
  return () => { cancel(); for (const [target, type, listener, options] of listeners) target.removeEventListener(type, listener, options) }
}

/** Callback ref handles late-mounted dialog portals; identity changes cancel stale gestures. */
export function useSurfaceGesture(resolve: Resolve, identity: string, enabled = true) {
  const [node, ref] = useState<HTMLElement | null>(null)
  const latest = useRef(resolve)
  const suppression = useRef(false)
  useLayoutEffect(() => { latest.current = resolve })
  useLayoutEffect(() => {
    if (node && enabled) return bindSurfaceGesture(node, (...args) => latest.current(...args), suppression)
  }, [node, identity, enabled])
  return { ref, node }
}
